import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IoCheckmark, IoChevronDown, IoClose } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import { FieldError } from "react-hook-form";

export interface Option {
  value: string | number;
  label: string;
  disabled?: boolean;
  meta?: {
    [key: string]: any;
  };
}

type Mode = "static" | "dynamic";

interface Props {
  mode?: Mode;
  options?: Option[];
  value?: string | number | (string | number)[];
  onChange: (val: any) => void;
  multiple?: boolean;
  onSearch?: (q: string, signal?: AbortSignal) => Promise<Option[]>;
  onRetry?: () => void;
  placeholder?: string;
  debounce?: number;
  minSearchLength?: number;
  readOnly?: boolean;
  clearable?: boolean;
  label?: string;
  id?: string;
  error?: FieldError;
  disabled?: boolean;
  defaultSelectedOptions?: Option[];
  defaultValue?: any;
  other?: boolean;
}

export default function SmartSelect({
  mode = "static",
  options = [],
  value,
  onChange,
  multiple = false,
  onSearch,
  onRetry,
  placeholder,
  debounce = 300,
  minSearchLength = 2,
  readOnly,
  clearable = true,
  label,
  id,
  error,
  disabled,
  defaultSelectedOptions = [],
  defaultValue,
  other,
}: Props) {
  const { t } = useTranslation();
  const DROPDOWN_HEIGHT = 320;

  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<Option[]>(options);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);

  const [selectedValue, setSelectedValue] = useState<Option | null>(null);

  const [selectedItemsCache, setSelectedItemsCache] = useState<
    Map<string | number, Option>
  >(new Map());

  const [pos, setPos] = useState({
    top: 0,
    left: 0,
    width: 0,
    placement: "bottom" as "bottom" | "top",
  });

  const source = mode === "static" ? options : items;

  useEffect(() => {
    if (multiple) {
      if (defaultSelectedOptions && defaultSelectedOptions.length > 0) {
        const values = defaultSelectedOptions.map((opt) => opt.value);
        if (!value || (Array.isArray(value) && value.length === 0)) {
          onChange(values);
        }

        const newCache = new Map();
        defaultSelectedOptions.forEach((opt) => {
          newCache.set(opt.value, opt);
        });
        setSelectedItemsCache(newCache);
      }
    } else {
      if (
        defaultValue &&
        typeof defaultValue === "object" &&
        defaultValue.default?.value !== undefined &&
        defaultValue.default?.value !== null &&
        defaultValue.default?.value !== ""
      ) {
        if (value !== defaultValue.default.value) {
          onChange(defaultValue.default.value);
        }
        setSelectedValue(defaultValue.default);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== static options =====
  useEffect(() => {
    if (mode === "static") {
      setItems(options);
    }
  }, [mode, options]);

  // =====  selectedValue  single mode =====
  useEffect(() => {
    if (!multiple) {
      if (value !== undefined && value !== null && value !== "") {
        const matchedOption = source.find((opt) => opt.value === value);
        if (matchedOption) {
          setSelectedValue(matchedOption);
        } else {
          const fromDefault = defaultValue?.default;
          if (fromDefault && fromDefault.value === value) {
            setSelectedValue(fromDefault);
          } else {
            const fromOptions = options.find((opt) => opt.value === value);
            if (fromOptions) {
              setSelectedValue(fromOptions);
            } else {
              setSelectedValue(null);
            }
          }
        }
      } else {
        setSelectedValue(null);
      }
    }
  }, [value, source, multiple, defaultValue, options]);

  // ===== multiple =====
  useEffect(() => {
    if (multiple && Array.isArray(value)) {
      const newCache = new Map(selectedItemsCache);

      value.forEach((val) => {
        if (!newCache.has(val)) {
          let option = source.find((opt) => opt.value === val);
          if (!option) {
            option = defaultSelectedOptions.find((opt) => opt.value === val);
          }
          if (!option) {
            option = options.find((opt) => opt.value === val);
          }
          if (option) {
            newCache.set(val, option);
          }
        }
      });

      const valueSet = new Set(value);
      for (const [key] of newCache) {
        if (!valueSet.has(key)) {
          newCache.delete(key);
        }
      }

      setSelectedItemsCache(newCache);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, source, multiple, defaultSelectedOptions, options]);

  // outside click
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      const target = e.target as Node;
      const clickedTrigger = triggerRef.current?.contains(target);
      const clickedDropdown = dropdownRef.current?.contains(target);

      if (!clickedTrigger && !clickedDropdown) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  // async search
  useEffect(() => {
    if (mode !== "dynamic" || !onSearch) return;
    if (query.length < minSearchLength) return;

    const handler = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        setLoading(true);
        const res = await onSearch(query, controller.signal);
        setItems(res || []);
        setTimeout(updatePosition, 50);
      } finally {
        setLoading(false);
      }
    }, debounce);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, mode]);

  // clear
  const clearValue = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(multiple ? [] : "");
    setSelectedValue(null);
    setQuery("");
    if (multiple) {
      setSelectedItemsCache(new Map());
    }
  };

  // filter static
  const filtered = useMemo(() => {
    if (mode === "dynamic") return items;

    return items.filter((o) =>
      o.label.toLowerCase().includes(query.toLowerCase())
    );
  }, [items, query, mode]);

  // selected helper
  const isSelected = (val: any) => {
    if (multiple) return Array.isArray(value) && value.includes(val);
    return value === val;
  };

  // toggle value
  const toggleValue = (val: any) => {
    if (multiple) {
      const arr = Array.isArray(value) ? value : [];
      const newValue = arr.includes(val)
        ? arr.filter((v) => v !== val)
        : [...arr, val];
      onChange(newValue);

      if (arr.includes(val)) {
        const newCache = new Map(selectedItemsCache);
        newCache.delete(val);
        setSelectedItemsCache(newCache);
      } else {
        const option =
          source.find((opt) => opt.value === val) ||
          options.find((opt) => opt.value === val);
        if (option) {
          const newCache = new Map(selectedItemsCache);
          newCache.set(val, option);
          setSelectedItemsCache(newCache);
        }
      }
    } else {
      onChange(val);
      setSelectedValue(null);
      setOpen(false);
    }
  };

  // keyboard
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) setOpen(true);

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActive((p) => (p < filtered.length - 1 ? p + 1 : p));
        break;

      case "ArrowUp":
        e.preventDefault();
        setActive((p) => Math.max(0, p - 1));
        break;

      case "Enter":
        e.preventDefault();
        const opt = filtered[active];
        if (opt) toggleValue(opt.value);
        break;

      case "Escape":
        setOpen(false);
        break;
    }
  };

  const updatePosition = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const GAP = 4;
    const dropdownHeight = Math.min(DROPDOWN_HEIGHT, filtered.length * 44 + 60);

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    const showBelow = spaceBelow > dropdownHeight + GAP;
    const showAbove = spaceAbove > dropdownHeight + GAP;

    let top: number;
    let placement: "bottom" | "top";

    if (showBelow) {
      top = rect.bottom + GAP;
      placement = "bottom";
    } else if (showAbove) {
      top = rect.top - dropdownHeight - GAP;
      placement = "top";
    } else {
      top = rect.bottom + GAP;
      placement = "bottom";
    }

    setPos({
      left: rect.left,
      width: rect.width,
      placement,
      top,
    });
  };

  useEffect(() => {
    if (!open) return;

    updatePosition();

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const el = itemRefs.current[active];
    if (!el) return;

    el.scrollIntoView({
      block: "nearest",
      behavior: "smooth",
    });
  }, [active]);

  useEffect(() => {
    if (open) {
      setTimeout(updatePosition, 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, open]);

  return (
    <div className="relative w-full">
      {label && (
        <label
          htmlFor={id}
          className="ml-3 text-sm font-bold text-navy-700 dark:text-white"
        >
          {label}
        </label>
      )}

      {/* TRIGGER */}
      <button
        ref={triggerRef}
        type="button"
        disabled={readOnly || disabled}
        onClick={() => setOpen((p) => !p)}
        className={`flex min-h-11 w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-4 text-sm text-gray-800 transition-all duration-200 hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/20 dark:bg-gray-900 dark:text-white dark:hover:border-white/40 dark:focus:border-blue-400 dark:focus:ring-blue-400/30 ${
          open
            ? "border-blue-500 ring-2 ring-blue-500/20 dark:border-blue-400"
            : ""
        } ${
          error
            ? "border-red-500 focus:border-red-500 focus:ring-red-500/30 dark:border-red-400"
            : ""
        }
`}
      >
        {/* SINGLE */}
        {!multiple && (
          <span className="truncate">
            {selectedValue
              ? selectedValue?.label
              : value === -1 && other
              ? t("actions.otherTitle")
              : source.find((o) => o?.value === value)?.label ||
                placeholder ||
                t("actions.choose")}
          </span>
        )}

        {/* MULTI */}
        {multiple && (
          <div className="flex flex-wrap gap-1 py-2">
            {Array.isArray(value) && value.length === 0 && (
              <span className="text-gray-400">
                {placeholder || t("actions.choose")}
              </span>
            )}

            {Array.isArray(value) &&
              value.map((val) => {
                const option =
                  selectedItemsCache.get(val) ||
                  source.find((o) => o.value === val) ||
                  options.find((o) => o.value === val) ||
                  defaultSelectedOptions.find((o) => o.value === val);

                if (!option) return null;

                return (
                  <span
                    key={val}
                    className="text-md flex items-center gap-1 rounded bg-brand-50 px-2 py-1"
                  >
                    {option.label}
                    <IoClose
                      className="cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        const newValue = value.filter((v) => v !== val);
                        onChange(newValue);

                        const newCache = new Map(selectedItemsCache);
                        newCache.delete(val);
                        setSelectedItemsCache(newCache);
                      }}
                    />
                  </span>
                );
              })}
          </div>
        )}

        <div className="group flex items-center gap-2">
          {/* CLEAR */}
          {clearable &&
            !readOnly &&
            ((multiple && Array.isArray(value) && value.length > 0) ||
              (!multiple && value)) && (
              <button
                type="button"
                onClick={clearValue}
                className="flex h-5 w-5 items-center justify-center rounded-full text-gray-400 hover:bg-red-100 hover:text-red-500"
              >
                <IoClose size={16} />
              </button>
            )}

          <IoChevronDown
            size={16}
            className={`transition ${open ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {/* DROPDOWN */}
      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            className="custom-scrollbar fixed z-[999999] max-h-[320px] overflow-y-auto rounded-2xl border bg-white shadow-2xl dark:bg-navy-700"
            style={{
              top: pos.top,
              left: pos.left,
              width: pos.width,
            }}
          >
            {/* SEARCH */}
            <div className="border-b p-3">
              <input
                autoFocus
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActive(0);
                }}
                onKeyDown={handleKeyDown}
                onMouseDown={(e) => e.stopPropagation()}
                placeholder={t("actions.search")}
                className="h-10 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-white/10 dark:bg-gray-800 dark:text-white dark:focus:border-blue-400 dark:focus:ring-blue-400/30"
              />
            </div>

            {/* LOADING */}
            {loading && (
              <div className="flex w-full items-center justify-center p-4">
                <div className="loader w-5 text-blue-700" />
              </div>
            )}

            {!loading &&
              filtered.length === 0 &&
              query.length >= minSearchLength && (
                <div className="flex w-full items-center justify-center p-4 text-sm text-gray-500">
                  {t("actions.noResults")}
                </div>
              )}

            {mode === "static" && multiple && (
              <>
                <button
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => {
                    const allValues = filtered.map((opt) => opt.value);
                    const currentValues = Array.isArray(value) ? value : [];
                    const allSelected = allValues.every((val) =>
                      currentValues.includes(val)
                    );
                    if (allSelected) {
                      const remaining = currentValues.filter(
                        (val) => !allValues.includes(val)
                      );
                      onChange(remaining);

                      const newCache = new Map(selectedItemsCache);
                      allValues.forEach((val) => newCache.delete(val));
                      setSelectedItemsCache(newCache);
                    } else {
                      const newValues = [
                        ...new Set([...currentValues, ...allValues]),
                      ];
                      onChange(newValues);

                      const newCache = new Map(selectedItemsCache);
                      filtered.forEach((opt) => {
                        newCache.set(opt.value, opt);
                      });
                      setSelectedItemsCache(newCache);
                    }
                  }}
                  className={`flex w-full items-center justify-between px-4 py-3 text-start text-sm transition-colors ${
                    Array.isArray(value) &&
                    filtered.every((opt) => value.includes(opt.value))
                      ? "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300"
                      : "text-gray-800 hover:bg-blue-50 dark:text-gray-300 dark:hover:bg-white/20"
                  }`}
                >
                  <span className="font-medium">{t("actions.allItems")}</span>
                  {Array.isArray(value) &&
                    filtered.every((opt) => value.includes(opt.value)) && (
                      <IoCheckmark
                        className="text-brand-500 dark:text-brand-400"
                        size={18}
                      />
                    )}
                </button>
                <div className="my-2 border-t border-gray-200 dark:border-gray-700" />
              </>
            )}

            {/* OPTIONS */}
            {!loading &&
              filtered.map((opt, i) => {
                const selected = isSelected(opt.value);

                return (
                  <button
                    key={opt.value}
                    ref={(el) => {
                      itemRefs.current[i] = el;
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => toggleValue(opt.value)}
                    onMouseEnter={() => setActive(i)}
                    className={`
                      flex w-full items-center justify-between px-4 py-3 text-start text-sm
                       ${
                         selected
                           ? "bg-brand-300 text-white"
                           : `
                           text-gray-800 hover:bg-brand-50
                           dark:text-gray-300 dark:hover:bg-white/10
                          ${
                            i === active ? "bg-brand-50 dark:bg-white/10" : ""
                          } `
                       }
                    `}
                  >
                    <span>{opt.label}</span>

                    {selected && (
                      <IoCheckmark
                        className={selected ? "text-white" : "text-brand-500"}
                      />
                    )}
                  </button>
                );
              })}

            {other && (
              <button
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => {
                  toggleValue(-1);
                }}
                className={`flex w-full items-center justify-between px-4 py-3 text-start text-sm text-gray-800 transition-colors hover:bg-blue-50 dark:text-gray-300 dark:hover:bg-white/20`}
              >
                <span className="font-medium">{t("actions.other")}</span>
              </button>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}
